import type { SidebarMenuItemVariantProps } from './SidebarMenuItem.variants';

type Size = NonNullable<SidebarMenuItemVariantProps['size']>;
type Highlight = NonNullable<SidebarMenuItemVariantProps['highlight']>;

/**
 * Leading (Icon-Left) size per row size — exact from Figma's per-size nodes
 * ("Left slot Content": XL 26px, L/M 22px, S 18px).
 */
export const LEFT_ICON_SIZE_FOR: Record<Size, number> = {
  xl: 26,
  l: 22,
  m: 22,
  s: 18,
};

/**
 * Chevron button ("Slot 5") footprint per row size, in px — must match the
 * `size-*` classes in `sidebarMenuItemChevronVariants`. Drives the tree indent
 * a chevron-less row reserves, so the two can't drift.
 *
 * Figma pins it at 32px on XL (5311:5348) and 28px on L/M/S (5311:5908 /
 * 5311:6468 / 5311:7020) — it does NOT scale with the row the way the label
 * and left icon do.
 */
export const CHEVRON_BUTTON_PX: Record<Size, number> = {
  xl: 32,
  l: 28,
  m: 28,
  s: 28,
};

/** Chevron glyph size per row size. */
export const CHEVRON_ICON_SIZE_FOR: Record<Size, number> = {
  xl: 18,
  l: 18,
  m: 18,
  s: 16,
};

// Icon tints mirror the label colour table in SidebarMenuItem.variants.ts —
// see the brand palette (brand-colors.json) for the token ↔ hex mapping.
const DEFAULT_ICON = { light: '#525252', dark: '#a1a1a1' }; // secondary-600
const ACTIVE_ICON = { light: '#262626', dark: '#e5e5e5' }; // secondary-800
const HIGHLIGHT_PRIMARY_ICON = { light: '#4d179a', dark: '#ede9fe' }; // primary-900

/**
 * Resolve the icon hex for an (active, highlight, dark) tuple. Used on
 * native, where `react-native-svg` needs an explicit `color` prop. On web the
 * component passes `currentColor` instead, so icons inherit the row's label
 * colour (including the pure-CSS hover shift).
 */
export function resolveSidebarMenuItemIconColor(
  active: boolean,
  highlight: Highlight,
  isDark: boolean,
): string {
  const pair =
    highlight === 'primary'
      ? HIGHLIGHT_PRIMARY_ICON
      : active
        ? ACTIVE_ICON
        : DEFAULT_ICON;
  return isDark ? pair.dark : pair.light;
}
